import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import api from "../../api"; // axios instance with token + school_id interceptor
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";


const SubjectPage = () => {
  const [form, setForm] = useState({ subject_name: "", code: "", subject_type: "main" });
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [msg, setMsg] = useState(null);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // API base path (interceptor already prepends /api)
  const baseURL = "/subjects";
  const schoolId = localStorage.getItem("school_id");

  // fetch subjects list
  const fetchSubjects = async () => {
    try {
      const res = await api.get(`${baseURL}/list?school_id=${schoolId}`);
      const data = res.data;
      const list = data?.data ?? [];
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error fetching subjects:", err);
      setItems([]);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);

    if (!form.subject_name || form.subject_name.trim() === "") {
      setMsg({ type: "error", text: "Subject name required" });
      return;
    }

    const payload = { ...form };

    try {
      let res;
      if (editId) {
        res = await api.post(`${baseURL}/update`, { ...payload, id: editId });
      } else {
        res = await api.post(`${baseURL}/add`, payload);
      }

      const json = res.data;

      if (json?.status) {
        setMsg({
          type: "success",
          text: json.message || (editId ? "Updated successfully" : "Added successfully"),
        });
      } else {
        setMsg({ type: "error", text: json?.message || "Operation failed" });
      }

      setForm({ subject_name: "", code: "" });
      setEditId(null);
      fetchSubjects();
    } catch (err) {
      console.error("Error saving subject:", err);
      setMsg({ type: "error", text: err.response?.data?.message || err.message || "Network error" });
    }
  };

  const handleEdit = (item) => {
    setForm({ subject_name: item.subject_name ?? "", code: item.code ?? "", subject_type: item.subject_type ?? "main", });
    setEditId(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subject?")) return;
    try {
      const res = await api.post(`${baseURL}/delete`, { id });
      const json = res.data;
      if (json?.status) {
        setMsg({ type: "success", text: json.message || "Deleted" });
      } else {
        setMsg({ type: "error", text: json?.message || "Delete failed" });
      }
      fetchSubjects();
    } catch (err) {
      console.error("Delete error", err);
      setMsg({ type: "error", text: err.response?.data?.message || err.message || "Network error" });
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reordered = Array.from(items);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    // update local UI
    setItems(reordered);

    // prepare payload for DB
    const orders = reordered.map((item, index) => ({
      id: item.id,
      sort_order: index + 1,
    }));

    // save order
    await api.post("/subjects/update_order", { orders });
  };


  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <div style={{ flex: 1, backgroundColor: "#f4f6f8" }}>
        <main
          style={{
            padding: "30px",
            maxWidth: "1000px",
            margin: "auto",
            marginTop: "0px",
          }}
        >
          {/* Add/Edit Subject Section */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>{editId ? "✏️ Edit Subject" : "➕ Add New Subject"}</h2>

            {msg && (
              <div style={{ marginBottom: 12, color: msg.type === "error" ? "#cc0000" : "#166534" }}>
                {msg.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={formGrid}>
                <FormGroup label="Subject Name">
                  <input
                    type="text"
                    name="subject_name"
                    value={form.subject_name}
                    onChange={handleChange}
                    style={fullWidthInput}
                    required
                  />
                </FormGroup>

                <FormGroup label="Code (optional)">
                  <input
                    type="text"
                    name="code"
                    value={form.code}
                    onChange={handleChange}
                    style={fullWidthInput}
                  />
                </FormGroup>
                <FormGroup label="Subject Type">
                  <select
                    name="subject_type"
                    value={form.subject_type}
                    onChange={handleChange}
                    style={fullWidthInput}
                  >
                    <option value="main">Main Subject</option>
                    <option value="extra">Extra Subject</option>
                    <option value="other">Other's Subject</option>
                  </select>
                </FormGroup>

              </div>

              <div style={{ textAlign: "right", marginTop: "20px" }}>
                <button type="submit" style={buttonStyle}>
                  {editId ? "Update Subject" : "Add Subject"}
                </button>
              </div>
            </form>
          </section>

          {/* List Subjects */}
          {/* List Subjects */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>📋 All Subjects</h2>

            {items.length === 0 ? (
              <p>No subjects found.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>#</th>
                      <th style={thStyle}>Subject Name</th>
                      <th style={thStyle}>Code</th>
                      <th style={thStyle}>Type</th>
                      <th style={thStyle}>Created At</th>
                      <th style={thStyle}>Actions</th>
                    </tr>
                  </thead>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="subjects">
                      {(provided) => (
                        <tbody ref={provided.innerRef} {...provided.droppableProps}>
                          {items.map((item, idx) => (
                            <Draggable key={item.id} draggableId={String(item.id)} index={idx}>
                              {(provided) => (
                                <tr
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    ...provided.draggableProps.style,
                                    background: idx % 2 === 0 ? "#f9fafb" : "#eef3f9",
                                  }}
                                >
                                  <td style={tdStyle}>{idx + 1}</td>
                                  <td style={tdStyle}>{item.subject_name}</td>
                                  <td style={tdStyle}>{item.code || "-"}</td>

                                  <td style={tdStyle}>
                                    <span
                                      style={{
                                        padding: "4px 10px",
                                        borderRadius: "12px",
                                        fontSize: "12px",
                                        background: item.subject_type === "main" ? "#d1fae5" : "#ffedd5",
                                        border: "1px solid",
                                      }}
                                    >
                                      {item.subject_type === "main" ? "Main" : item.subject_type === "extra" ? "Extra" : "Other"}
                                    </span>
                                  </td>

                                  <td style={tdStyle}>{item.created_at}</td>

                                  <td style={tdStyle}>
                                    <FaEdit
                                      onClick={() => handleEdit(item)}
                                      style={{ cursor: "pointer", color: "#0077cc" }}
                                    />
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </tbody>
                      )}
                    </Droppable>
                  </DragDropContext>

                </table>
              </div>
            )}
          </section>

        </main>
      </div>
    </div>
  );
};

// Reusable Form Group
const formGrid = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "20px",
};

const fullWidthInput = {
  width: "100%",
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: "15px",
  boxSizing: "border-box",
};

const FormGroup = ({ label, children, style }) => (
  <div style={{ ...style }}>
    <label style={{ marginBottom: "6px", display: "block", fontWeight: 500, color: "#444" }}>{label}</label>
    {children}
  </div>
);

const buttonStyle = {
  padding: "10px 25px",
  backgroundColor: "#0077cc",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontSize: "15px",
  cursor: "pointer",
};

const cardStyle = {
  background: "#ffffff",
  padding: "25px",
  borderRadius: "12px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  marginBottom: "30px",
};

const titleStyle = {
  color: "#0077cc",
  marginBottom: "25px",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "15px",
  background: "#fff",
  borderRadius: "8px",
  overflow: "hidden",
};

const thStyle = {
  padding: "12px",
  textAlign: "left",
  background: "#0077cc",
  color: "white",
  fontWeight: "600",
  fontSize: "14px",
};

const tdStyle = {
  padding: "12px",
  fontSize: "14px",
  color: "#333",
  borderBottom: "1px solid #e5e7eb",
};

const trEven = {
  background: "#f9fafb",
};

const trOdd = {
  background: "#eef3f9",
};

export default SubjectPage;
